import React, { useState, useRef } from 'react';
import {
  Container,
  Checkbox,
  ExpandableSection,
  Header,
  FormField,
  SpaceBetween,
  Textarea,
  Button,
  Box,
  Alert,
} from '@cloudscape-design/components';
import { InfoLink } from '../../commons/common-components';
import validateField from '../form-validation-config';
import { submissionsApi } from '../../../api/Submission';
import BroadcastSubscriptionAdapter from './BroadcastSubscriptionAdapter';
import { Spinner } from '@cloudscape-design/components';
import CustomModal from './custom-modal';

function DistributionsFooter({ state, onChange }) {
  return (
    <ExpandableSection headerText="Additional settings" variant="footer">
      <SpaceBetween size="l">
        <FormField label="IPv6">
          <Checkbox checked={state.ipv6isOn} onChange={({ detail: { checked } }) => onChange('ipv6isOn', checked)}>
            Turn on
          </Checkbox>
        </FormField>
      </SpaceBetween>
    </ExpandableSection>
  );
}

const isS3PermissionError = (attribute, errorText) =>
  attribute === 's3BucketSelectedOption' &&
  errorText ===
    "CloudFront isn't allowed to write logs to this bucket. You must enable access control lists (ACL) for the bucket.";

export default function DistributionPanel({
  loadHelpPanelContent,
  validation = false,
  data,
  errors = {},
  setData,
  setErrors,
  refs = {},
}) {
  const [isCustomModalVisible, setIsCustomModalVisible] = useState(false);
  const [customModalMessage, setcustomModalMessage] = useState('Formal Arrangements could be policies, procedures, bylaws, handbooks or manuals with a clear description of the processs steps, the timeframe, the stakeholders involved, and their roles and responsibilities. Evidence on the implementation is required in case the formal arrangement is already implemented.');

  const [processingFiles, setprocessingFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [tokenLimitError, setTokenLimitError] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(null);
  const fileInputRef = useRef(null);

  //  const MAX_FILES_LIMIT = 5;

  const onChange = (attribute, value) => {
    setData({ [attribute]: value });

    if (validation && errors[attribute]?.length > 0) {
      const { errorText } = validateField(attribute, value);

      if (isS3PermissionError(attribute, errorText)) {
        setErrors({ [attribute]: '' });
      } else {
        setErrors({ [attribute]: errorText });
      }
    }
  };
 
  const onBlur = attribute => {
    if (!validation) {
      return;
    }

    const value = data[attribute];
    const { errorText } = validateField(attribute, value);

    if (isS3PermissionError(attribute, errorText)) {
      return;
    }

    setErrors({ [attribute]: errorText });
  };

  const uploadFile = async (file, indicatorSubmissionId) => {
    let upload_id = null;
    try {
      const uploadUrlResponse = await submissionsApi.generateUploadUrl(
        indicatorSubmissionId,
        { filename: file.name }
      );
      upload_id = uploadUrlResponse.upload_id;
      const { presigned_url } = uploadUrlResponse;

      const uploadResponse = await fetch(presigned_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': 'application/octet-stream'
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }
      
      const completedUpload = await submissionsApi.markUploadComplete(upload_id);

      setTokenUsage(completedUpload.used_tokens_limit);
      return {
        success: true,
        fileId: upload_id,
        ...completedUpload
      };
  
    } catch (error) {
      if (upload_id) {
        try {
          await submissionsApi.deleteUploadedFile(upload_id);
        } catch (deleteError) {
          console.error('Failed to cleanup failed upload:', deleteError);
        }
      }

      if (error.response && error.response.status === 400) {
        if (error.response.data?.detail?.error_code === 4) {
          const { max_tokens, current_tokens } = error.response.data.detail;
          const message = `Token limit exceeded. Maximum allowed: ${max_tokens} Token, current total: ${current_tokens} Token`;
          setTokenLimitError(message) ;
          return 
        }
        if (error.response.data?.detail?.error_code === 5) {
          const message = `Maximum number of files exceeded. You can upload up to 5 files per indicator.`;
          setTokenLimitError(message);
          return 
        }
      }
      
      console.error('Upload error:', error);
      throw error;
    }
  };
  
  const validateFile = (file) => {
    const errors = [];
    const allowedExtensions = ['.txt', '.csv', '.png', '.jpg', '.pdf', '.docx', '.xlsx'];
    const maxSize = 15 * 1024 * 1024; // MB
    const extension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(extension)) {
      errors.push(`File extension ${extension} is not supported`);
    }
    
    if (file.size > maxSize) {
      errors.push('File size exceeds 15MB limit');
    }
    
    return errors;
  }

  const deleteFile = async (file) => {
    setprocessingFiles([file.name]);
    await submissionsApi.deleteUploadedFile(file.fileID);
    setprocessingFiles([]);

    setData({
      ...data,
      evidences: data.evidences.filter(f => f.fileID !== file.fileID)
    });
  }

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    setTokenLimitError(null);
  
    if (!file) return;

    const currentFileCount = data.evidences ? data.evidences.length : 0;

    const validationErrors = validateFile(file);
    if (validationErrors.length > 0) {
      const errorMessage = (
        <div>
          The file has issues:<br />
          {validationErrors.map((err, i) => (
            <div key={i} style={{ marginLeft: '20px' }}>- {err}</div>
          ))}
        </div>
      );
      setErrors(errorMessage);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setprocessingFiles([file.name]);
    
    try {
      const result = await uploadFile(file, data.id);
      if (result && result.fileId) {
        setData({
          ...data,
          evidences: [...data.evidences, {
            fileID: result.fileId,
            file: file
          }]
        });
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error(`Failed to upload ${file.name}:`, error);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setprocessingFiles([]);
    }
  };
  
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setTokenLimitError(null);

    const filesWithErrors = [];
    const validFiles = [];

    for (const file of selectedFiles) {
      const validationErrors = validateFile(file);
      if (validationErrors.length > 0) {
        filesWithErrors.push({ file, errors: validationErrors });
      } else {
        validFiles.push(file);
      }
    }

    if (filesWithErrors.length > 0) {
      const errorMessage = <div>
        The following files have issues:<br/>
        {filesWithErrors.map(f => (
          <div key={f.file.name}>
            • {f.file.name}:<br/>
            {f.errors.map((err, i) => (
              <div key={i} style={{ marginLeft: '20px' }}>- {err}</div>
            ))}
          </div>
        ))}
      </div>;
      setErrors(errorMessage);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSelectedFiles([]);
    }

    if (validFiles.length > 0) {
      setprocessingFiles(validFiles.map(f => f.name));
      const successfulUploads = [];
      
      try {
        for (const file of validFiles) {
          try {
            const result = await uploadFile(file, data.id);
            if (result && result.fileId) {
              successfulUploads.push({
                file,
                uploadId: result.fileId,
              });
            }
          } catch (fileError) {
            console.error(`Failed to upload ${file.name}:`, fileError);
          }
        }

        if (successfulUploads.length > 0) {
          setData({
            ...data,
            evidences: [...data.evidences, ...successfulUploads.map(r => ({
              fileID: r.uploadId,
              file: r.file
            }))]
          });
        }

        setSelectedFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        console.error('Upload process failed:', error);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setSelectedFiles([]);
      } finally {
        setprocessingFiles([]);
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB';
    else return (bytes / 1048576).toFixed(2) + ' MB';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Container
      id="distribution-panel"
      header={<Header variant="h2">{data.indicator?.index}: {data.indicator?.name }</Header>}
    >
      <SpaceBetween size="l">
        <Container
          header={
            <Header
              variant="h2"
              description={data.indicator?.description}
            >
              Description
            </Header>
          }
        ></Container>
        <Container
          header={
            <Header
              variant="h2"
              description={data.indicator?.extracted_admin_checklist?.map((line, index) => (
                  <p key={index} className="mt-2">
                    {line}
                  </p>
                ))}
            >
              Indicator Checklist
            </Header>
          }
        ></Container>

        <FormField
          label={
            <>
              Institution's Description 
            </>
          }
          info={<InfoLink id="institution-description-info" onFollow={()=>setIsCustomModalVisible(true)}/>}
          description="Describe your institution's formal arrangements related to this indicator"
          stretch={true}
          errorText={errors.text}
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          <Textarea
            rows={10}
            value={data.text}
            onChange={({ detail: { value } }) => onChange('text', value)}
            onBlur={() => onBlur('text')}
            ref={refs.text}
          />
        </FormField>

        <FormField
          label="Evidences"
          description="Upload the evidence supporting your institution's formal arrangements related to this indicator"
          info={<InfoLink id="institution-description-info" onFollow={()=>setIsCustomModalVisible(true)}/>}
          errorText={errors.evidences}
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          <SpaceBetween size="m">
            <div>
              <input
                type="file"
                id="file-upload-input"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".txt,.csv,.png,.jpg,.pdf,.docx,.txt,.xlsx"
                style={{ marginBottom: '10px' }}
                disabled={processingFiles.length > 0}
              />
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                You can upload related documents in the following format (.docx, .txt, .csv, .xlsx, .png, .jpg, .pdf)
              </div>
            </div>
            
            {tokenLimitError && (
              <Alert type="error">
                {tokenLimitError}
              </Alert>
            )}
            
            {tokenUsage !== null && (
              <Alert type="info">
                Current token usage: {tokenUsage}%
              </Alert>
            )}
            
            {data.evidences.length > 0 && (
              <Box padding="s" backgroundColor="light">
                <h4 style={{ marginTop: 0 }}>Uploaded Files</h4>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {data.evidences.map((evidence, index) => (
                    <li key={index} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          padding: '8px 0',
                          borderBottom: index < data.evidences.length - 1 ? '1px solid #eee' : 'none'
                        }}>
                          <div>
                            <div>{evidence.file.name}</div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Button 
                              variant="icon" 
                              iconName="download" 
                              ariaLabel={`Download ${evidence.file.name}`}
                              onClick={async () => {
                                try {
                                  const response = await submissionsApi.downloadUploadedFile(evidence.fileID);
                                  const link = document.createElement('a');
                                  link.href = response.url;
                                  link.download = evidence.file.name;
                                  link.click();
                                } catch (err) {
                                  console.error('Download error:', err);
                                  alert('Failed to download file');
                                }
                              }}
                            />
                            <Button 
                              variant="icon" 
                              iconName="remove" 
                              onClick={() => deleteFile(evidence)}
                              ariaLabel={`Remove ${evidence.file.name}`}
                            />
                          </div>
                        </li>

                  ))}
                </ul>
              </Box>
            )}
          </SpaceBetween>
        </FormField>
        
        <FormField>
          {processingFiles.length > 0 && (
            <span className="text-sm text-gray-600">
              <Spinner size="normal" /> Processing {processingFiles.length} file(s)...
            </span>
          )}
        </FormField>
        
        <BroadcastSubscriptionAdapter submissionId={data.id} />
        <CustomModal
          visible={isCustomModalVisible}
          onDismiss={() => setIsCustomModalVisible(false)}
          message={customModalMessage}
        />
      </SpaceBetween>
    </Container>
  );
}
